import type { HelioScoreResult, PropertyMapResult, QuoteTier } from "@/api/types";
import { isLiveStage, remoteSurveyIsComplete, type ProjectProgressInput } from "@/constants/projectStages";

export type ConfidenceStage = "personalised" | "verified" | "connected";

export const PILLAR_DISPLAY = [
  { apiKey: "generation_potential", label: "Generation Potential", nickname: "Your roof", fixed: true, weight: 0.25 },
  { apiKey: "economic_opportunity", label: "Economic Opportunity", nickname: "The money", fixed: false, weight: 0.25 },
  { apiKey: "energy_independence", label: "Energy Independence", nickname: "Off the grid", fixed: false, weight: 0.2 },
  { apiKey: "property_performance", label: "Property Performance", nickname: "The building", fixed: true, weight: 0.1 },
  { apiKey: "smart_energy_readiness", label: "Smart Energy Readiness", nickname: "Smart ready", fixed: false, weight: 0.1 },
  { apiKey: "optimisation_potential", label: "Optimisation Potential", nickname: "System fit", fixed: false, weight: 0.1 },
] as const;

export type PillarDisplay = {
  apiKey: (typeof PILLAR_DISPLAY)[number]["apiKey"];
  label: string;
  nickname: string;
  fixed: boolean;
  weight: number;
  score: number;
};

const BANDS = [
  { min: 80, label: "Excellent", color: "#D4FF00" },
  { min: 70, label: "Strong", color: "#B4EC3F" },
  { min: 58, label: "Good", color: "#F06AD8" },
  { min: 45, label: "Fair", color: "#B063F7" },
] as const;

export const SCORE_BANDS = [
  { label: "Limited", color: "#7D6EFF", width: 45 },
  { label: "Fair", color: "#B063F7", width: 13 },
  { label: "Good", color: "#F06AD8", width: 12 },
  { label: "Strong", color: "#B4EC3F", width: 10 },
  { label: "Excellent", color: "#D4FF00", width: 20 },
] as const;

export function bandFor(score: number): { label: string; color: string } {
  for (const band of BANDS) {
    if (score >= band.min) return { label: band.label, color: band.color };
  }
  return { label: "Limited", color: "#7D6EFF" };
}

export type CatalogOption = QuoteTier & {
  systemOptionId: number;
  panels: number;
};

export function sizedCatalog(catalog: QuoteTier[] | null | undefined): CatalogOption[] {
  return (catalog ?? []).filter(
    (option): option is CatalogOption =>
      typeof option.systemOptionId === "number"
      && typeof option.panels === "number"
      && option.panels > 0,
  );
}

export function uniquePanelCounts(options: CatalogOption[]): number[] {
  return [...new Set(options.map((option) => option.panels))].sort((a, b) => a - b);
}

/** Next priced panel count above or below a roof total, including totals between catalog sizes. */
export function adjacentPanelCount(
  counts: readonly number[],
  current: number,
  direction: "down" | "up",
): number | null {
  if (!Number.isFinite(current)) return null;
  const ordered = counts.filter((count) => Number.isFinite(count)).slice().sort((a, b) => a - b);
  if (direction === "down") {
    let lower: number | null = null;
    for (const count of ordered) {
      if (count < current) lower = count;
    }
    return lower;
  }
  return ordered.find((count) => count > current) ?? null;
}

export function uniqueBatteryKwh(options: CatalogOption[]): number[] {
  return [...new Set(options.map((option) => option.batteryKwh))].sort((a, b) => a - b);
}

function closestBy<T>(items: T[], value: number, read: (item: T) => number): T {
  return items.reduce((best, item) =>
    Math.abs(read(item) - value) < Math.abs(read(best) - value) ? item : best,
  );
}

/** Snap a panel count (and optional battery) onto a priced catalog row. */
export function snapCatalogOption(
  options: CatalogOption[],
  panels: number,
  batteryKwh?: number | null,
): CatalogOption | null {
  if (options.length === 0 || !Number.isFinite(panels)) return null;
  const panelTarget = closestBy(uniquePanelCounts(options), panels, (count) => count);
  const samePanels = options.filter((option) => option.panels === panelTarget);
  const pool = samePanels.length > 0 ? samePanels : options;
  if (batteryKwh == null || !Number.isFinite(batteryKwh)) {
    return closestBy(pool, panels, (option) => option.panels);
  }
  return closestBy(pool, batteryKwh, (option) => option.batteryKwh);
}

export function tierKeyFor(option: QuoteTier): "good" | "better" | "best" {
  if (option.tier === "good" || option.tier === "better" || option.tier === "best") {
    return option.tier;
  }
  return "good";
}

export function confidenceFromProject(
  project: Pick<ProjectProgressInput, "stage" | "remoteSurveyStatus" | "remoteSurveySubmittedAt"> | null | undefined,
): ConfidenceStage {
  if (!project) return "personalised";
  if (isLiveStage(project.stage)) return "connected";
  if (project.remoteSurveySubmittedAt || remoteSurveyIsComplete(project)) return "verified";
  return "personalised";
}

export function scorePillars(score: HelioScoreResult | null | undefined): PillarDisplay[] {
  if (!score?.pillars) return [];
  const rows: PillarDisplay[] = [];
  for (const pillar of PILLAR_DISPLAY) {
    const raw = score.pillars[pillar.apiKey];
    if (typeof raw?.score !== "number") continue;
    rows.push({
      apiKey: pillar.apiKey,
      label: pillar.label,
      nickname: pillar.nickname,
      fixed: pillar.fixed,
      weight: typeof raw.weight === "number" ? raw.weight : pillar.weight,
      score: raw.score,
    });
  }
  return rows;
}

export function formatRoofArea(area?: number | null): string | null {
  if (area == null || !Number.isFinite(area)) return null;
  return `${Math.round(area)} m²`;
}

export function formatAzimuth(insights: PropertyMapResult["solarInsights"] | null | undefined): string | null {
  if (!insights) return null;
  if (insights.roofOrientation?.trim()) {
    const degrees = insights.azimuthDegrees;
    if (degrees != null && Number.isFinite(degrees)) {
      return `${insights.roofOrientation} (${Math.round(degrees)}°)`;
    }
    return insights.roofOrientation;
  }
  if (insights.azimuthDegrees != null && Number.isFinite(insights.azimuthDegrees)) {
    return `${Math.round(insights.azimuthDegrees)}°`;
  }
  return null;
}

export function formatGbp(value: number, decimals = 0): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatShading(factor?: number | null): string | null {
  if (factor == null || !Number.isFinite(factor)) return null;
  const pct = Math.round(factor * 100);
  return `${pct}% sun`;
}
